import React, { useEffect, useState } from "react";
import {
    Box, Typography, Paper, Table, TableBody, TableCell, 
    TableContainer, TableHead, TableRow, Button, Stack, 
    IconButton, Dialog, DialogTitle, DialogContent, 
    DialogActions, Chip, Autocomplete, TextField
} from "@mui/material";
import { Edit, Save, EventNote } from "@mui/icons-material";
import { collection, getDocs, doc, setDoc } from "firebase/firestore";
import { db } from "../../firebase";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const MEAL_TYPES = ["Breakfast", "Lunch", "Snacks", "Dinner"];

function Mealplanning() {
    const [weeklyMenu, setWeeklyMenu] = useState({});
    const [canteenItems, setCanteenItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    
    const [selectedDay, setSelectedDay] = useState("Monday");
    const [formData, setFormData] = useState({
        Breakfast: [],
        Lunch: [],
        Dinner: [],
        Snacks: []
    });

    useEffect(() => {
        loadCanteenItems();
        loadWeeklyMenu();
    }, []);

    const loadCanteenItems = async () => {
        const snap = await getDocs(collection(db, "canteenmenu"));
        // Only fetch items marked as available
        const items = snap.docs
            .map(d => d.data())
            .filter(item => item.isAvailable !== false)
            .map(item => item.name);
        setCanteenItems(items);
    };

    const loadWeeklyMenu = async () => {
        setLoading(true);
        try {
            const snap = await getDocs(collection(db, "mealplanning_weekly"));
            const menuMap = {};
            snap.docs.forEach(d => {
                menuMap[d.id] = d.data();
            });
            setWeeklyMenu(menuMap);
        } catch (e) { console.error(e); }
        setLoading(false);
    };

    const handleEditDay = (day) => {
        setSelectedDay(day);
        // Ensure every meal type is an array even if old data was a string
        const existingData = weeklyMenu[day] || {};
        setFormData({
            Breakfast: Array.isArray(existingData.Breakfast) ? existingData.Breakfast : [],
            Lunch: Array.isArray(existingData.Lunch) ? existingData.Lunch : [],
            Dinner: Array.isArray(existingData.Dinner) ? existingData.Dinner : [],
            Snacks: Array.isArray(existingData.Snacks) ? existingData.Snacks : []
        });
        setOpen(true);
    };

    const handleSave = async () => {
        try {
            await setDoc(doc(db, "mealplanning_weekly", selectedDay), {
                ...formData,
                updatedAt: new Date().toISOString(),
                schoolId: "zIlbJM5BjyrlEhg7fWKs"
            });
            setOpen(false);
            loadWeeklyMenu();
        } catch (e) { console.error(e); }
    };

    return (
        <Box p={2}>
            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 3, bgcolor: '#fbfbfb' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <EventNote color="primary" />
                    <Typography variant="h6" fontWeight="bold">Weekly Mess Schedule</Typography>
                </Stack>
            </Paper>

            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                    <TableHead sx={{ bgcolor: '#f0f2f5' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', width: 120 }}>Day</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Breakfast</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Lunch</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Snacks</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Dinner</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {DAYS.map((day) => (
                            <TableRow key={day} hover>
                                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>{day}</TableCell>
                                {MEAL_TYPES.map((type) => (
                                    <TableCell key={type}>
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                            {weeklyMenu[day]?.[type]?.length > 0 ? (
                                                weeklyMenu[day][type].map((item, idx) => (
                                                    <Chip key={idx} label={item} size="small" sx={{ fontSize: '10px', height: 20 }} />
                                                ))
                                            ) : (
                                                <Typography variant="caption" color="text.disabled">-</Typography>
                                            )}
                                        </Stack>
                                    </TableCell>
                                ))}
                                <TableCell align="right">
                                    <IconButton size="small" onClick={() => handleEditDay(day)}>
                                        <Edit fontSize="small" color="primary" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Edit {selectedDay} Menu</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={3} py={1}>
                        {MEAL_TYPES.map((type) => (
                            <Autocomplete
                                key={type}
                                multiple
                                options={canteenItems}
                                value={formData[type]}
                                onChange={(event, newValue) => {
                                    setFormData({ ...formData, [type]: newValue });
                                }}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip label={option} size="small" {...getTagProps({ index })} />
                                    ))
                                }
                                renderInput={(params) => (
                                    <TextField {...params} label={type} placeholder="Select items" size="small" />
                                )}
                            />
                        ))}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button variant="contained" startIcon={<Save />} onClick={handleSave}>Save Weekly Plan</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Mealplanning;